import { axios } from 'common/utils'
import { isMobileEnv, isPcEnv } from 'common/utils/common'
import { SuggestionType } from 'common/constants'
import { SuggestionResponse, CatalogResponse } from '../types/entities/api/autoComplete'
import { StorageKeywordKey } from '../types/storage'
import { ArrayUtils } from '@shopping/common'

const suggestionCache = { '': { data: {}, totalCount: 0, keywordQuery: [] } }
const catalogCache = {} // 원부(catalog) 캐시 데이터

const RECENT_TYPE = 'recent' // 최근 검색어 저장할 키
const MAX_KEYWORD_MALL_COUNT = 10 // 키워드 타입 + 몰 타입 최대 노출 갯수
const MAX_RECENT_COUNT = 3 // 최근 검색어 최대 노출 갯수

const getGroupByType = items => {
  const findFirstIndex = items.findIndex(item => item.length > 0)
  const startIndex = findFirstIndex < 4 ? 0 : 5
  let mallType = []
  let keywordType = []

  mallType = isMobileEnv
    ? [
        ...mallType,
        ...items[startIndex + 2].slice(0, 2).map(item => ({
          keyword: item[0] && item[0][0],
          mallSeq: item[2] && item[2][0],
        })),
      ]
    : []

  const keywordCount = MAX_KEYWORD_MALL_COUNT - mallType.length
  keywordType = [
    ...keywordType,
    ...items[startIndex + 1].slice(0, keywordCount).map(item => ({
      keyword: item[0] && item[0][0],
      hasCatalog: item[3] && item[3][0],
    })),
  ]

  // 윈도우: 최대 1개 노출
  // 몰: 최대 2개 노출
  // 키워드 + 몰 타입 합쳐서 최대 10개 노출
  // 기획전: 최대 1개 노출
  // 카테고리 : 최대 2개 노출
  return {
    [SuggestionType.WINDOW]: items[startIndex].slice(0, 1).map(item => ({
      keyword: item[0] && item[0][0],
      url: item[1] && item[1][0],
      name: item[2] && item[2][0],
    })),
    [SuggestionType.KEYWORD]: keywordType,
    [SuggestionType.MALL]: mallType,
    [SuggestionType.PLAN]: items[startIndex + 3].slice(0, 1).map(item => ({
      keyword: item[0] && item[0][0],
      date: item[1] && item[1][0],
      url: isPcEnv ? item[2] && item[2][0] : isMobileEnv ? item[3] && item[3][0] : '',
      imageUrl: item[4] && item[4][0],
    })),
    [SuggestionType.CATEGORY]: items[startIndex + 4].slice(0, 2).map(item => ({
      keyword: item[0] && item[0][0],
      categoryId: item[1] && item[1][0],
    })),
  }
}

const getCount = items => {
  return Object.keys(items).reduce((prev, key) => {
    return prev + items[key].length
  }, 0)
}

// 자동 완성 데이터 중 최근 검색어에 포함되어 있는 키워드는 따로 담는다
// UI에 특정 위치에 위치해야함 (윈도 키워드 아래. 가장 최상단, 최대 3개)
// 최근 검색어는 1차원 배열 [ {keyword: '', keywordType: '', ...} ]
// 자동 완성 데이터 형태는 { [타입별]: [{keyword: '', keywordType: '', ...}] }
const filterRecentKeywords = (suggestions, recentKeywords) => {
  recentKeywords.map(recentKeyword => {
    // 자동완성 타입별로 전부 검사하지 않고, 최근검색어 내에 존재하는 타입만 꺼내와서 검사한다 -> 따로 타입 체크 하지 않아도 된다
    const type = recentKeyword.keywordType
    const autocompleteByType = suggestions[type]
    const currentSize = suggestions[RECENT_TYPE].length // 최대 3개만 저장

    autocompleteByType.map((autocompleteKeyword, index) => {
      if (recentKeyword.keyword === autocompleteKeyword.keyword && currentSize < MAX_RECENT_COUNT) {
        autocompleteKeyword.type = type
        autocompleteKeyword.isLatest = true
        autocompleteKeyword.searchDate = recentKeyword.date
        suggestions[type].splice(index, 1) // 해당 데이터를 원본 데이터에서 제거한다
        suggestions[RECENT_TYPE] = [...suggestions[RECENT_TYPE], autocompleteKeyword]
      }
    })
  })
  return suggestions
}

export const fetchSuggestions = async (
  keyword: string,
  recentKeywords?: StorageKeywordKey[],
): Promise<{
  keyword: string
  data: object
  keywordQuery: string[]
  totalCount: number
}> => {
  let totalCount = 0

  if (!suggestionCache[keyword]) {
    const result = await axios.get<SuggestionResponse>('/v1/autocomplete/suggestions', { params: { keyword } })

    if (result && result.data) {
      const { query, items } = result.data

      let groupByType = items && getGroupByType(items)

      if (isMobileEnv) {
        // 최근 검색어 기능은 모바일에만 존재
        groupByType[RECENT_TYPE] = [] // 자동완성 리스트 내의 최근 검색어들을 담는다
        groupByType = filterRecentKeywords(groupByType, recentKeywords)
      } else {
        if (groupByType && groupByType['window'] && ArrayUtils.isNotEmpty(groupByType['window'])) {
          groupByType['window'] = groupByType['window'].filter(windowType => windowType.name !== 'selective')
        }
      }

      totalCount = getCount(groupByType) // 최대 노출 갯수 적용 이후에 계산

      suggestionCache[keyword] = {
        totalCount,
        keywordQuery: query,
        data: totalCount > 0 ? groupByType : [],
      }
    }
  }

  return {
    keyword,
    ...suggestionCache[keyword],
  }
}

export const fetchCatalog = async (keyword: string): Promise<string> => {
  if (!catalogCache[keyword]) {
    const result = await axios.get<CatalogResponse>('/v1/autocomplete/catalogs', { params: { keyword } })
    catalogCache[keyword] = result && result.data
  }

  return catalogCache[keyword]
}
